<template>
	<div>
		<div v-if="!consentActive" class="alert alert-danger text-center">
			<strong>Inactive</strong>
		</div>
		<div class="card">
			<div class="card-header">
				<h3>
					<a href="/consents">Consents:</a> {{ consent.name }}
				</h3>
			</div>
			<div class="card-body">
				<div class="row" style="margin-bottom: 15px;">
					<div class="col-sm-6">
						<input class="form-control" type="text" v-model="filter" placeholder="Search participants by CCS ID or name" @input="fetchDebounced">
					</div>
					<div class="col-sm-6 text-right">
						<button v-if="consentActive" class="btn btn-primary" :disabled="selectedParticipants.length === 0" @click="bulkAssignTasks">Assign Consent Task</button>
						<span v-if="!consentActive" class="text-danger"><em>Task assignment is disabled for inactive consents.</em></span>
					</div>
				</div>

				<div v-if="loading" class="loading-overlay" :style="{'height': `${containerHeight}px`, 'width': `${containerWidth}px`}">
					<div class="loading-inner">
						<span class="sr-only">Loading...</span>
					</div>
				</div>
				<table class="table" id="consent-detail-table">
					<thead>
						<tr>
							<th>
								<span class="sort" @click="applySort('ccs_id')">
                                    <span>CCS ID</span>
                                    <down-arrow v-if="sortDirection == 'asc' || sortField != 'ccs_id'" /> 
                                    <up-arrow v-else />
                                </span>
							</th>
							<th>
								<span class="sort" @click="applySort('first_name')">
                                    <span>Name</span>
                                    <down-arrow v-if="sortDirection == 'asc' || sortField != 'first_name'" /> 
                                    <up-arrow v-else />
                                </span>
                            </th>
							<th>
								<span class="sort" @click="applySort('age')">
                                    <span>Age</span>
                                    <down-arrow v-if="sortDirection == 'asc' || sortField != 'age'" /> 
                                    <up-arrow v-else />
                                </span>
							</th>
							<th>
								<span class="sort" @click="applySort('is_spanish_speaking')">
                                    <span>Language</span>
                                    <down-arrow v-if="sortDirection == 'asc' || sortField != 'is_spanish_speaking'" /> 
                                    <up-arrow v-else />
                                </span>
							</th>
							<th>
								<span class="sort" @click="applySort('last_signed')">
                                    <span>Last {{ consent.consent_type.slug == 'main' ? 'Main Consent' : '' }} Signed</span>
                                    <down-arrow v-if="sortDirection == 'asc' || sortField != 'last_signed'" /> 
                                    <up-arrow v-else />
                                </span>
							</th>
							<th v-if="consentActive" scope="col" class="col-2">Task</th>
							<th v-if="consentActive">
								<input type="checkbox" 
									id="select-all-checkbox"
									v-model="allChecked"
									@input="toggleAllSelectedParticipants"
								>
							</th>
						</tr>
					</thead>
					<tbody>
						<tr v-for="participant in participants" :key="participant.id">
							<td>{{ participant.ccs_id }}</td>
							<td><a :href="'/participants/'+participant.id">{{ participant.full_name }}</a></td>
							<td>{{ participant.age }}</td>
							<td>{{ participant.language }}</td>
							<td>{{ consent.consent_type.slug == 'main' ? participant.last_signed_name : participant.last_signed_date }}</td>
							<td v-if="participant.current_task == null && consentActive">
								<button class="btn btn-sm btn-primary" 
									@click="assignSingleTask(participant.id)" 
									:disabled="selectedParticipants.length !== 0">
									Assign Consent Task
								</button>
							</td>
							<td v-if="participant.current_task != null && consentActive">
								<a :href="'/tasks/'+participant.current_task.id" class="text-wrap">{{ consent.name }}</a>
								<br />
								<button class="btn btn-sm btn-default" 
									@click="cancelTask(participant.full_name, participant.current_task.id)">
									cancel
								</button>
							</td>
							<td v-if="consentActive">
								<input type="checkbox" 
									:id="'checkbox_'+participant.id" 
									:value="participant.id"
									:disabled="participant.current_task != null"
									v-model="selectedParticipants"
								>
							</td>
						</tr>
					</tbody>
				</table>
			</div>
		</div>
	</div>
</template>

<script setup>
import { ref, onMounted, toRef, unref, watch } from 'vue'
import _ from 'lodash'
import DownArrow from '../shared/DownArrow.vue'
import UpArrow from '../shared/UpArrow.vue'

const props = defineProps(['consent'])

const loading = ref(true)
const containerHeight = ref(0)
const containerWidth = ref(0)
const allChecked = ref(false)
const participants = ref([])
const errors = ref([])
const selectedParticipants = ref([])
const filter = ref(null)
const sortField = ref('ccs_id')
const sortDirection = ref('asc')
const consentActive = toRef(props.consent.active)

onMounted(() => {
	fetchParticipants()
})

async function fetchParticipants() {
	loading.value = true

    try {
        const res = await axios.get('/api/consents/'+props.consent.id+'/participants', {
            params: {
                sortField: unref(sortField),
                sortDirection: unref(sortDirection),
                filter: unref(filter)
            }
        })
		participants.value = res.data.data
		selectedParticipants.value = []
    }
    catch (e) {
        console.log("Error: fetchParticipants(), "+ e.message)   
    }
    finally {
		loading.value = false
    }
}

const fetchDebounced = _.debounce(function() {
    fetchParticipants()
}, 500)

function applySort(field) {
	if(field == unref(sortField)) {
        sortDirection.value = unref(sortDirection) == 'asc' ? 'desc' : 'asc'
    } else {
    	sortDirection.value = 'asc'
    	sortField.value = field
    }
	fetchDebounced()
}

function assignSingleTask(id) {
	selectedParticipants.value = [id]
	bulkAssignTasks()
}

function bulkAssignTasks() {
    loading.value = true
    let url = '/api/consents/'+props.consent.id+'/assignTask'

    axios.post(url, {'participant_ids': unref(selectedParticipants)})
        .then((response) => {
            fetchParticipants()
            allChecked.value = false
        }).catch((e) => {
        	loading.value = false
        	errors.value = e.response.data.message
        })
}

function cancelTask(part_name, task_id) {
	let confirmationMessage = 'You are about to cancel a consent task for '+part_name+'. Are you sure you want to continue?'
	if(!confirm(confirmationMessage)) return;

    loading.value = true
    let url = '/api/tasks/'+task_id

    axios.patch(url, {task_status_id: 5})
        .then((response) => {
            fetchParticipants()
        }).catch((e) => {
        	loading.value = false
        	errors.value = e.response.data.message
        })
}

function toggleAllSelectedParticipants() {
    if (unref(allChecked)) {
        selectedParticipants.value = []
    } else {
        let partIds = []
        unref(participants).forEach(function (participant) {
            if (participant.current_task == null) {
                partIds.push(participant.id)
            }
        })
        selectedParticipants.value = partIds
    }
}

watch(
  () => unref(loading),
  (loading) => {
    const el = document.getElementById('consent-detail-table')
	containerHeight.value = loading && el ? el.offsetHeight : 0
	containerWidth.value = loading && el ? el.offsetWidth : 0
  }
)
</script>

<style scoped>
.sort {
	cursor: pointer;
}
.loading-inner {
    width: 100%;
    height: 100%;
}
.loading-overlay {
	width: 100%;
    height: 100%;
    position: absolute;
	z-index: 10;
	background: #000000;
	opacity: 0.2;
}
</style>